import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useOktaAuth } from '@okta/okta-react';
import * as Sentry from '@sentry/react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearAuth } from '@/store/slices/authSlice';
import logger from '@/utils/Logger';

export const useAuth = () => {
  const { oktaAuth } = useOktaAuth();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, isLoading } = useAppSelector((state) => state.auth);

  const login = useCallback(async () => {
    try {
      const transactionExists = await oktaAuth.tx.exists();

      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'info',
        message: 'Login redirect initiated',
        data: {
          path: location.pathname,
          transactionExists
        }
      });

      logger.info('Initiating login redirect');

      oktaAuth.setOriginalUri(location.pathname + location.search);

      // Capture event before redirect to avoid breadcrumb loss
      Sentry.captureMessage('Login redirect initiated', {
        contexts: {
          auth: {
            pathname: location.pathname,
            transactionExists
          }
        }
      });

      await oktaAuth.signInWithRedirect().catch((error) => {
        logger.error('useAuth: signInWithRedirect failed', error as Error);
        Sentry.captureException(error);
        // Re-throw the error so caller can handle it appropriately
        throw error;
      });
    } catch (error) {
      // Catch any errors from tx.exists() or other async operations
      logger.error('useAuth: redirect preparation failed', error as Error);
      Sentry.captureException(error);
      throw error;
    }
  }, [oktaAuth, location.pathname, location.search]);

  const logout = useCallback(() => {
    oktaAuth.signOut().then(() => {
      dispatch(clearAuth());
    });
  }, [oktaAuth, dispatch]);

  return { isAuthenticated, user, isLoading, login, logout };
};

export default useAuth;
